import { useEffect, useMemo, useRef } from "react";

// Fire-and-forget analytics for the candidate voice-interview pages
// (DynamicVoiceInterviewMain and ScheduleStage). Tracking must never block,
// delay or break the candidate flow: every failure is swallowed into
// console.error and nothing is retried.

const ENDPOINT = `${process.env.REACT_APP_AI_API_KEY}candidate-events`;
const APP_KEY = process.env.REACT_APP_CANDIDATE_CHAT_API_KEY || "";
const FLUSH_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;
const MAX_ERROR_REASON = 200;

export const JOURNEY_SURFACES = {
  DV_PRESTART: "dynamic_voice_prestart",
  DV_CALL: "dynamic_voice_call",
  WEB_PRESTART: "web_call_prestart",
  WEB_CALL: "web_call_call",
};

// crypto.randomUUID only exists in secure contexts on newer browsers.
const uuid = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// One attempt per tab visit: sessionStorage keeps it across a refresh, while
// reopening the link later starts a new one. Falls back to a per-load id when
// storage is blocked (private mode, in-app WebViews).
const getAttemptId = (context) => {
  const key = `journey_attempt:${context.type}:${context.id}`;
  try {
    let id = sessionStorage.getItem(key);
    if (!id) {
      id = uuid();
      sessionStorage.setItem(key, id);
    }
    return id;
  } catch {
    return uuid();
  }
};

const stripQuery = (url) => url.split("?")[0].split("#")[0];

export function createJourneyTracker(context) {
  const attemptId = getAttemptId(context);
  let queue = [];

  function flush() {
    if (!queue.length) return;
    const events = queue;
    queue = [];
    try {
      fetch(ENDPOINT, {
        method: "POST",
        keepalive: true, // survives page close; sendBeacon can't send x-api-key
        headers: { "Content-Type": "application/json", "x-api-key": APP_KEY },
        body: JSON.stringify({
          context,
          attempt_id: attemptId,
          events,
          client: { page_url: stripQuery(window.location.href), user_agent: navigator.userAgent },
        }),
      })
        .then(async (res) => {
          if (!res.ok) {
            console.error("journey tracking failed", res.status);
            return;
          }
          if (process.env.NODE_ENV === "development") {
            const body = await res.json().catch(() => null);
            const rejected = body?.data?.rejected;
            if (rejected?.length) console.warn("journey tracking rejected events", rejected);
          }
        })
        .catch((err) => console.error("journey tracking failed", err));
    } catch (err) {
      console.error("journey tracking failed", err);
    }
  }

  function track(name, surface, { outcome, error_code, error_reason, properties } = {}) {
    try {
      queue.push({
        event_id: uuid(),
        name,
        surface,
        occurred_at: new Date().toISOString(),
        ...(outcome && { outcome }),
        ...(error_code && { error_code: String(error_code).slice(0, 64) }),
        ...(error_reason && { error_reason: String(error_reason).slice(0, MAX_ERROR_REASON) }),
        ...(properties && { properties }),
      });
      if (queue.length >= FLUSH_SIZE) flush();
    } catch (err) {
      console.error("journey tracking failed", err);
    }
  }

  const timer = setInterval(flush, FLUSH_INTERVAL_MS);
  const onHide = () => document.visibilityState === "hidden" && flush();
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", onHide);

  function destroy() {
    flush();
    clearInterval(timer);
    window.removeEventListener("pagehide", flush);
    document.removeEventListener("visibilitychange", onHide);
  }

  return { track, flush, destroy, attemptId };
}

// Creates one tracker per page for the given context (null = not known yet)
// and destroys it on unmount. The returned { track, flush } is stable across
// renders and silently no-ops while there's no context.
export function useJourneyTracker(context) {
  const trackerRef = useRef(null);
  const contextKey = context ? `${context.type}:${context.id}:${context.secret || ""}` : "";

  useEffect(() => {
    if (!context) return undefined;
    const tracker = createJourneyTracker(context);
    trackerRef.current = tracker;
    return () => {
      tracker.destroy();
      if (trackerRef.current === tracker) trackerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);

  return useMemo(
    () => ({
      track: (...args) => trackerRef.current?.track(...args),
      flush: () => trackerRef.current?.flush(),
    }),
    []
  );
}
